import React, { useEffect, useState } from "react";
import {
  NavLink,
  useLocation,
  useNavigate,
  matchPath,
  Link,
} from "react-router-dom";
import axios from "axios";
import "./HomeNavBar.css";

const HamburgMenuOpen = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3 12H21M3 6H21M3 18H21"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const HamburgMenuClose = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M18 6L6 18M6 6L18 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const HomeNavBar = () => {
  const [dashboardURL, setDashboardURL] = useState("/");
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const location = useLocation();

  const [mobileSubmissionOpen, setMobileSubmissionOpen] = useState(false);
  const [mobileVenueOpen, setMobileVenueOpen] = useState(false);
  const [click, setClick] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Handle window resize for responsiveness
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      // Close mobile menu when screen becomes larger
      if (window.innerWidth > 992) {
        setClick(false);
        setMobileSubmissionOpen(false);
        setMobileVenueOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setClick(false);
    setMobileSubmissionOpen(false);
    setMobileVenueOpen(false);
  }, [location.pathname]);

  // Handle body scroll when mobile menu is open
  useEffect(() => {
    if (click && windowWidth <= 992) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [click, windowWidth]);

  useEffect(() => {
    if (token) {
      axios
        .get(
          `${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/sign-in/signin/getdashboard`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then((response) => {
          setDashboardURL(response.data.dashboardURL);
        })
        .catch((error) => {
          console.error("Error fetching the dashboard URL:", error);
        });
    }
  }, [token]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    localStorage.removeItem("email");
    navigate("/login");
  };

  const handleClick = () => {
    setClick(!click);
    if (click) {
      setMobileSubmissionOpen(false);
      setMobileVenueOpen(false);
    }
  };

  const handleLinkClick = () => {
    setClick(false);
    setMobileSubmissionOpen(false);
    setMobileVenueOpen(false);
  };

  const handleSubmissionToggle = (e) => {
    e.preventDefault();
    // On mobile, toggle dropdown; on desktop, let CSS handle hover
    if (windowWidth <= 992) {
      setMobileSubmissionOpen(!mobileSubmissionOpen);
      setMobileVenueOpen(false);
    }
  };

  const handleVenueToggle = (e) => {
    e.preventDefault();
    // On mobile, toggle dropdown; on desktop, let CSS handle hover
    if (windowWidth <= 992) {
      setMobileVenueOpen(!mobileVenueOpen);
      setMobileSubmissionOpen(false);
    }
  };

  const isPathActive = (paths) => {
    return paths.some(
      (path) => matchPath({ path, end: true }, location.pathname) !== null
    );
  };

  const isSubmissionActive = isPathActive([
    "/accepted-paper",
    "/submission-guideline",
  ]);

  const isVenueActive = isPathActive([
    "/venu-details",
    "/venue-accomodations",
    "/tourist-place",
  ]);

  return (
    <div className="nav-container-wrapper">
      <nav className="modern-navbar">
        <div className="nav-content">
          {/* Logo/Brand */}
          <Link to="/" className="nav-brand">
            <img src="/logo.png" alt="Conference Logo" className="brand-logo" />
            <span className="brand-text">ETSD</span>
          </Link>

          {/* Desktop Menu */}
          <ul className={`nav-menu ${click ? "nav-menu-active" : ""}`}>
            <li className="nav-item">
              <NavLink
                to="/"
                className={({ isActive }) =>
                  `nav-link ${isActive ? "active" : ""}`
                }
                onClick={handleLinkClick}
              >
                Home
              </NavLink>
            </li>

            <li className="nav-item">
              <NavLink
                to="/committee"
                className={({ isActive }) =>
                  `nav-link ${isActive ? "active" : ""}`
                }
                onClick={handleLinkClick}
              >
                Committees
              </NavLink>
            </li>

            <li className="nav-item">
              <NavLink
                to="/dates"
                className={({ isActive }) =>
                  `nav-link ${isActive ? "active" : ""}`
                }
                onClick={handleLinkClick}
              >
                Dates
              </NavLink>
            </li>

            {/* Submission Dropdown */}
            <li
              className={`nav-item nav-dropdown ${
                isSubmissionActive ? "active" : ""
              }`}
            >
              <span
                className="nav-link nav-dropdown-toggle"
                onClick={handleSubmissionToggle}
              >
                Submission
              </span>
              <ul
                className={`nav-dropdown-menu ${
                  mobileSubmissionOpen ? "mobile-dropdown-open" : ""
                }`}
              >
                <li className="nav-dropdown-item">
                  <NavLink
                    to="/accepted-paper"
                    className={({ isActive }) =>
                      `nav-dropdown-link ${isActive ? "active" : ""}`
                    }
                    onClick={handleLinkClick}
                  >
                    Accepted Abstract
                  </NavLink>
                </li>
                <li className="nav-dropdown-item">
                  <NavLink
                    to="/submission-guideline"
                    className={({ isActive }) =>
                      `nav-dropdown-link ${isActive ? "active" : ""}`
                    }
                    onClick={handleLinkClick}
                  >
                    Submission Guideline
                  </NavLink>
                </li>
              </ul>
            </li>

            <li className="nav-item">
              <NavLink
                to="/registration"
                className={({ isActive }) =>
                  `nav-link ${isActive ? "active" : ""}`
                }
                onClick={handleLinkClick}
              >
                Registration
              </NavLink>
            </li>

            <li className="nav-item">
              <NavLink
                to="/schedules"
                className={({ isActive }) =>
                  `nav-link ${isActive ? "active" : ""}`
                }
                onClick={handleLinkClick}
              >
                Program
              </NavLink>
            </li>

            <li className="nav-item">
              <NavLink
                to="/speakers"
                className={({ isActive }) =>
                  `nav-link ${isActive ? "active" : ""}`
                }
                onClick={handleLinkClick}
              >
                Speakers
              </NavLink>
            </li>

            {/* Venue Dropdown */}
            <li
              className={`nav-item nav-dropdown ${
                isVenueActive ? "active" : ""
              }`}
            >
              <span
                className="nav-link nav-dropdown-toggle"
                onClick={handleVenueToggle}
              >
                Venue
              </span>
              <ul
                className={`nav-dropdown-menu ${
                  mobileVenueOpen ? "mobile-dropdown-open" : ""
                }`}
              >
                <li className="nav-dropdown-item">
                  <NavLink
                    to="/venu-details"
                    className={({ isActive }) =>
                      `nav-dropdown-link ${isActive ? "active" : ""}`
                    }
                    onClick={handleLinkClick}
                  >
                    Venue Details
                  </NavLink>
                </li>
                <li className="nav-dropdown-item">
                  <NavLink
                    to="/venue-accomodations"
                    className={({ isActive }) =>
                      `nav-dropdown-link ${isActive ? "active" : ""}`
                    }
                    onClick={handleLinkClick}
                  >
                    Accommodations
                  </NavLink>
                </li>
                <li className="nav-dropdown-item">
                  <NavLink
                    to="/tourist-place"
                    className={({ isActive }) =>
                      `nav-dropdown-link ${isActive ? "active" : ""}`
                    }
                    onClick={handleLinkClick}
                  >
                    Tourist Places
                  </NavLink>
                </li>
              </ul>
            </li>

            <li className="nav-item">
              <NavLink
                to="/contact"
                className={({ isActive }) =>
                  `nav-link ${isActive ? "active" : ""}`
                }
                onClick={handleLinkClick}
              >
                Contact
              </NavLink>
            </li>

            {/* Auth Section */}
            <li
              className={`nav-item auth-section ${
                token ? "logged-in" : "logged-out"
              }`}
            >
              {token ? (
                <>
                  <NavLink
                    to={dashboardURL}
                    className={({ isActive }) =>
                      `nav-link dashboard-link ${isActive ? "active" : ""}`
                    }
                    onClick={handleLinkClick}
                  >
                    Dashboard
                  </NavLink>
                  <button
                    onClick={handleLogout}
                    className="nav-link logout-btn"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <NavLink
                  to="/login"
                  className={({ isActive }) =>
                    `nav-link login-btn ${isActive ? "active" : ""}`
                  }
                  onClick={handleLinkClick}
                >
                  Login
                </NavLink>
              )}
            </li>
          </ul>

          {/* Mobile Menu Toggle */}
          <div className="mobile-menu-toggle" onClick={handleClick}>
            {click ? <HamburgMenuClose /> : <HamburgMenuOpen />}
          </div>
        </div>
      </nav>
    </div>
  );
};

export default HomeNavBar;
